import React from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, 
  Clock, 
  Headphones, 
  Heart, 
  Target, 
  CheckCircle, 
  Phone,
  MessageCircle,
  FileText
} from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

export default function Process() {
  const { t, currentLanguage } = useTranslation();
  
  // Функция для получения правильного пути в зависимости от языка
  const getLocalizedPath = (path) => {
    if (currentLanguage === 'ru') {
      return path === '/' ? '/ru' : `/ru${path}`;
    }
    return path;
  };
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  const steps = [
    {
      icon: Phone,
      title: t('process.step1.title'),
      duration: t('process.step1.duration'),
      description: t('process.step1.description'),
      details: [
        t('process.step1.detail1'),
        t('process.step1.detail2'),
        t('process.step1.detail3')
      ]
    },
    {
      icon: MessageCircle,
      title: t('process.step2.title'),
      duration: t('process.step2.duration'),
      description: t('process.step2.description'),
      details: [
        t('process.step2.detail1'),
        t('process.step2.detail2'),
        t('process.step2.detail3')
      ]
    },
    {
      icon: FileText,
      title: t('process.step3.title'),
      duration: t('process.step3.duration'),
      description: t('process.step3.description'),
      details: [
        t('process.step3.detail1'),
        t('process.step3.detail2'),
        t('process.step3.detail3')
      ]
    },
    {
      icon: CheckCircle,
      title: t('process.step4.title'),
      duration: t('process.step4.duration'),
      description: t('process.step4.description'),
      details: [
        t('process.step4.detail1'),
        t('process.step4.detail2'),
        t('process.step4.detail3')
      ]
    }
  ];

  const preparationSteps = [
    {
      icon: Clock,
      title: t('process.preparation.time.title'),
      description: t('process.preparation.time.description')
    },
    {
      icon: Headphones,
      title: t('process.preparation.equipment.title'),
      description: t('process.preparation.equipment.description')
    },
    {
      icon: Heart,
      title: t('process.preparation.mindset.title'),
      description: t('process.preparation.mindset.description')
    },
    {
      icon: FileText,
      title: t('process.preparation.documents.title'),
      description: t('process.preparation.documents.description')
    }
  ];

  return (
    <section id="process" className="section-gray">
      <div className="container-spacious">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <motion.h2 variants={item} className="text-3xl md:text-4xl font-bold mb-6 text-dark-900">
            {t('process.title')}
          </motion.h2>
          <motion.p variants={item} className="text-body max-w-3xl mx-auto">
            {t('process.subtitle')}
          </motion.p>
        </motion.div>

        {/* Основные этапы */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="grid lg:grid-cols-2 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                variants={item}
                className="card-elevated p-8 relative"
              >
                {/* Номер этапа */}
                <div className="absolute top-4 right-4 w-10 h-10 bg-rose-100 text-rose-600 rounded-full flex items-center justify-center text-lg font-medium">
                  {index + 1}
                </div>

                {/* Иконка и заголовок */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-rose-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-dark-900">{step.title}</h3>
                    <p className="text-rose-600 font-medium">{step.duration}</p>
                  </div>
                </div>

                {/* Описание */}
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {step.description}
                </p>

                {/* Детали */}
                <ul className="space-y-2">
                  {step.details.map((detail, detailIndex) => (
                    <li key={detailIndex} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>


              </motion.div>
            ))}
          </div>
        </motion.div>


        {/* Подготовка к сессии */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <motion.h3 variants={item} className="text-xl font-bold text-center mb-8 text-dark-900">
            {t('process.preparation.title')}
          </motion.h3>
          <motion.p variants={item} className="text-center mb-8 text-gray-600">
            {t('process.preparation.subtitle')}
          </motion.p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {preparationSteps.map((step, index) => (
              <motion.div
                key={index}
                variants={item}
                className="card-elevated p-6 text-center"
              >
                <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-8 h-8 text-rose-600" />
                </div>
                <h4 className="text-lg font-semibold mb-2 text-dark-900">{step.title}</h4>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Призыв к действию */}
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <motion.div variants={item} className="block-primary text-center">
            <h3 className="heading-3 mb-4">
              {t('process.cta.title')}
            </h3>
            <p className="text-body mb-6">
              {t('process.cta.description')}
            </p>
            <a
              href={getLocalizedPath("/contact")}
              className="btn-primary"
            >
              {t('process.cta.button')}
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
